import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  TypographyProps,
  TextFieldProps,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
//import { useAppSelector } from '@/shared/hooks/redux';

interface EditableTextProps {
  value: string;
  onSave: (newValue: string) => Promise<void> | void;
  canEdit?: boolean;
  isLoading?: boolean;
  typographyProps?: TypographyProps;
  textFieldProps?: Omit<TextFieldProps, 'value' | 'onChange'>;
  multiline?: boolean;
  placeholder?: string;
  allowEmpty?: boolean;
}

export const EditableText: React.FC<EditableTextProps> = ({
  value,
  onSave,
  canEdit = true,
  isLoading = false,
  typographyProps = {},
  textFieldProps = {},
  multiline = false,
  placeholder = '',
  allowEmpty = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState('');
  //const isMobile = useAppSelector((state) => state.ui.isMobile);
  const isMobile = false; // for testing

  const handleStartEdit = () => {
    setEditedValue(value);
    setIsEditing(true);
  };

  const handleSave = async () => {
    const trimmedValue = editedValue.trim();
    if (!allowEmpty && !trimmedValue) return;

    try {
      await onSave(trimmedValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (!isEditing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Typography {...typographyProps} sx={{ mr: 1, ...typographyProps.sx }}>
          {value || placeholder}
        </Typography>
        {canEdit && !isMobile && (
          <IconButton size="small" onClick={handleStartEdit} disabled={isLoading} sx={{ ml: 1 }}>
            <EditIcon />
          </IconButton>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
      <TextField
        value={editedValue}
        onChange={(e) => setEditedValue(e.target.value)}
        variant="outlined"
        size="small"
        fullWidth
        multiline={multiline}
        rows={multiline ? 2 : 1}
        autoFocus
        onKeyDown={handleKeyDown}
        {...textFieldProps}
      />
      <Box sx={{ display: 'flex', flexDirection: multiline ? 'column' : 'row', gap: 1 }}>
        <IconButton
          size="small"
          onClick={handleSave}
          disabled={isLoading || (!allowEmpty && !editedValue.trim())}
          color="primary"
        >
          <SaveIcon />
        </IconButton>
        <IconButton size="small" onClick={handleCancel} disabled={isLoading}>
          <CancelIcon />
        </IconButton>
      </Box>
    </Box>
  );
};
